/**
 * The sign-up assessment's shapes (docs/signup-optimisation-work-orders.md,
 * "Shared contract"): the read payloads stored on gbp_agent_v1_signup_reads,
 * the market set rows, and the `summary` the screens render.
 *
 * Every field named in the contract is here under the contract's name. The
 * few additions are marked "added": each is a value a builder needs that the
 * contract's shape could not carry (the Maps feature id the posts read needs,
 * the address the profile audit needs), never a renamed field.
 */

export type KeywordRole = 'primary' | 'money' | 'category';
/** Where a search came from: a tracked keyword, what the owner typed on Your business, or a Google category of the profile. */
export type KeywordSource = 'tracked' | 'typed' | 'category';

export interface AssessmentKeyword {
  keyword: string;
  role: KeywordRole;
  volume: number | null;
  volume_market: string | null;
  /** Missing on assessments made before 31 Oct 26 evening. */
  source?: KeywordSource;
  /**
   * Role 'money' only: the seat among the up to three money searches (Anand,
   * 01 Nov 26, contract C1): 1 to 3, built from the owner's first three picks
   * in their tick order. Missing on assessments made before 01 Nov 26.
   */
  money_rank?: 1 | 2 | 3;
}

/**
 * The searches Fly tracks and grids (contract C1, 01 Nov 26): the primary and
 * the money ones. The bare category word ("dental clinic") is kept for the
 * market read and the volumes line only and gets no grid. Pure.
 */
export function hasGrid(k: Pick<AssessmentKeyword, 'role'>): boolean {
  return k.role !== 'category';
}

export type AssessmentKind = 'signup' | 'weekly' | 'manual';
export type ReadStatus = 'running' | 'done' | 'failed' | 'skipped';

/** One business in the `market` read: a maps_search item of the category at the pin. */
export interface MarketItem {
  place_id: string;
  cid: string | null;
  title: string;
  category: string | null;
  lat: number | null;
  lng: number | null;
  distance_km: number | null;
  rating: number | null;
  votes: number | null;
  photos: number | null;
  main_image: string | null;
  website: string | null;
  is_claimed: boolean | null;
  /** added: the Maps feature id ("0x...:0x..."), which the posts read asks by. */
  feature_id: string | null;
  /** added: the address as Maps writes it, for the profile audit. */
  address: string | null;
  /** added: rank_absolute at the pin, in the call that found it. */
  rank: number | null;
  /** added: the zoom of the call that found it (15, or 13 for the widening call). */
  zoom: number;
}

export interface MarketPayload {
  keyword: string;
  coordinate: string;
  items: MarketItem[];
}

export interface VolumesPayload {
  market_code: number | null;
  market_label: string | null;
  volumes: Record<string, number | null>;
}

export interface PackItem {
  rank: number;
  title: string;
  place_id: string | null;
  cid: string | null;
  rating: number | null;
  votes: number | null;
}

export interface PackPayload {
  keyword: string;
  items: PackItem[];
}

export interface GridListing {
  rank: number;
  place_id: string | null;
  cid: string | null;
  title: string;
  rating: number | null;
  votes: number | null;
}

export interface GridSquare {
  /** Index on the grid: 0 is the north-west square, 12 the centre, 24 the south-east. */
  n: number;
  lat: number;
  lng: number;
  no_data: boolean;
  listings: GridListing[];
}

export interface GridPayload {
  keyword: string;
  zoom: number;
  spacing_km: number;
  squares: GridSquare[];
}

export interface ReviewRow {
  date: string | null;
  rating: number | null;
  text: string | null;
  author_name: string | null;
  owner_reply: { date: string | null; text: string } | null;
}

export interface ReviewsPayload {
  place_id: string;
  rows: ReviewRow[];
  returned: number;
}

export interface PostsFacts {
  count_30d: number;
  count_90d: number;
  last_at: string | null;
}

export interface BusyFacts {
  peak_day: string | null;
  peak_hour: string | null;
  present: boolean;
}

export interface PostsBusyPayload {
  posts: Record<string, PostsFacts | null>;
  busy: Record<string, BusyFacts | null>;
  /**
   * added (Anand, 01 Nov 26): the owner's photo count on Google from the
   * business info read, set only when the market search did not return the
   * owner (posts-busy.ts readOwnInfo); absent otherwise.
   */
  own_photos?: number | null;
}

export interface AuditPayload {
  place_id: string;
  score: number | null;
  code: string | null;
  tries: number;
}

export interface WebsitePayload {
  place_id: string;
  url: string | null;
  score: number | null;
  /** added: generate-website-local-seo's overall_score (full: false). */
  local_seo_score: number | null;
  /** added: DataForSEO's onpage_score for the home page, rounded. */
  onpage_score: number | null;
}

/** A row of gbp_agent_v1_market_businesses, as the assessment reads it back. */
export interface MarketBusiness {
  place_id: string;
  cid: string | null;
  name: string;
  category: string | null;
  lat: number | null;
  lng: number | null;
  distance_km: number | null;
  photo_url: string | null;
  website: string | null;
  rating: number | null;
  reviews_count: number | null;
  photos_count: number | null;
  is_self: boolean;
  is_owner_pick: boolean;
  pack_rank: number | null;
  pack_keyword: string | null;
  /** 1..5, the seat among the owner's five (is_owner_pick); null for every other row. Absent on rows kept before 31 Oct 26 (null). */
  pick_rank: number | null;
}

/** The words reply quality looks for: services and treatments, and the area (with the business's own name). */
export interface ReplyWords {
  treat: string[];
  area: string[];
}

export interface ReplyQuality {
  /** Share of the latest reviews (up to 100) with an owner reply, 0 to 100. */
  answered: number;
  /** Share of replies naming a treatment or service. */
  treat: number;
  /** Share of replies naming the area or the business. */
  area: number;
  /** Share of replies using the reviewer's first name. */
  name: number;
  /** Share of distinct reply texts. */
  uniq: number;
  median_reply_days: number | null;
  score: number;
}

export interface ShareOfVoice {
  total: number;
  rows: Array<{ place_id: string; sov_pct: number; sov_rank: number }>;
}

export interface GridStats {
  cells: Array<number | null>;
  /** added: squares that answered (not no_data). */
  valid: number;
  found: number;
  top3: number;
  top10: number;
  median: number | null;
  best: number | null;
  holders: Array<{ place_id: string | null; title: string; count: number }>;
}

export type TileState = 'ok' | 'warn' | 'bad' | 'na';

export interface FreshnessTile {
  key: string;
  /** added: the tile's name as the owner reads it. */
  label: string;
  state: TileState;
  fact: string;
  fly: string;
  five: Array<{ place_id: string; state: TileState }>;
}

export type CellState = 'lead' | 'trail' | 'warn' | 'dim' | null;

export interface SummaryCell {
  place_id: string;
  value: number | string | null;
  small: string | null;
  state: CellState;
  /** added: 'lead' when the small text itself leads (reviews: +15 or more in 30 days). */
  small_state?: 'lead' | null;
  ni: boolean;
}

export interface SummaryRow {
  key: string;
  label: string;
  sub: string | null;
  cells: SummaryCell[];
}

export interface SummaryColumn {
  place_id: string;
  name: string;
  logo: string | null;
  is_self: boolean;
  is_owner_pick: boolean;
  pack_rank: number | null;
}

export interface GridHolder {
  place_id: string | null;
  name: string;
  logo: string | null;
  top3: number;
  /** added: that business's cells, so the screen can draw its grid beside the owner's. */
  cells: Array<number | null>;
}

export interface Summary {
  read_at: string;
  headline: string;
  lead: string[];
  need_improvement: Array<{ key: string; text: string; level: 'bad' | 'warn' }>;
  pack: { keyword: string; items: Array<{ rank: number; place_id: string | null; name: string; logo: string | null; rating: number | null; votes: number | null }>; self_in: boolean; self_rank: number | null } | null;
  keywords: AssessmentKeyword[];
  grids: Record<string, { self: GridStats; best_other: GridHolder | null; pack_leader: GridHolder | null }>;
  columns: SummaryColumn[];
  rows: SummaryRow[];
  google_only: {
    found_by: number | null;
    /** added: the month found_by counts ("Sep 26"). */
    found_by_month: string | null;
    search_views: number | null;
    search_views_prev: number | null;
    actions_28d: number | null;
    calls: number | null;
    directions: number | null;
    website_clicks: number | null;
    maps_views: number | null;
    /** added: all views in the 28 days and the 28 days before (views_28d value and detail.prev). */
    views_28d: number | null;
    views_28d_prev: number | null;
  } | null;
  freshness: FreshnessTile[];
  market: {
    total_reviews: number;
    count: number;
    self_rank: number | null;
    rows: Array<{
      place_id: string;
      name: string;
      logo: string | null;
      /** added */
      is_self: boolean;
      sov_pct: number;
      sov_rank: number;
      reviews: number;
      reviews_30d: number | null;
      answered_pct: number | null;
      reply_quality: number | null;
      squares_top3: number | null;
      pack_rank: number | null;
      in_set: 'five' | 'pack' | 'market';
    }>;
  } | null;
  /** added: the owner's reviews waiting for an answer on Google (gmb_reviews), for the plan's week 1. */
  self_waiting: number | null;
  costs: { usd: number; calls: number };
  /** The next four weeks (contract C2, the `calendar` read's payload); null until that read is done. */
  calendar: CalendarPayload | null;
}

/**
 * One row of the four weeks (contract C2): a post with its topic and caption,
 * or a work row with its title. `trial` true: done on the 14-day trial;
 * false: recommended, done on the plan.
 */
export interface CalendarItem {
  /** yyyy-mm-dd, the location's local day. */
  date: string;
  kind: 'post' | 'work';
  /** post: the keyword or the service in the locality, lower case ("root canal treatment in rahatani"). */
  topic?: string;
  /** post: the tracked search this post works, when it is one of the four; else null. */
  keyword?: string | null;
  /** post: one sentence, at most 90 characters, the post's headline as the owner reads it. */
  caption?: string;
  /** work: what Fly does that day ("Local rank check: the 25 spots read"). */
  title?: string;
  trial: boolean;
}

export interface CalendarWeek {
  /** 0 is today (the first hour); 1 to 4 start on the Mondays after day 0. */
  week: 0 | 1 | 2 | 3 | 4;
  /** yyyy-mm-dd: day 0 for week 0, a Monday for weeks 1 to 4. */
  starts_on: string;
  items: CalendarItem[];
}

/** The `calendar` read (contract C2, written by the start action after the searches are re-picked). */
export interface CalendarPayload {
  /** yyyy-mm-dd, the read's local day (day 0). */
  from: string;
  /** The location's zone (first-hour-post.ts zoneOf). */
  tz: string;
  /** ISO weekdays the agent posts on, 1 Mon to 7 Sun; [1, 3, 5] by default. */
  post_days: number[];
  weeks: CalendarWeek[];
  /** The text model that wrote the captions; null when the model call failed and every caption is the rule caption. */
  model: string | null;
}

export interface PlanItem {
  title: string;
  state: 'done' | 'recommended' | 'plan';
}

export interface PlanWeek {
  week: number;
  /** yyyy-mm-dd, a Monday. */
  starts_on: string;
  /** "Week 1, 02 Nov". */
  label: string;
  items: PlanItem[];
}
